import { hexToRgba } from '@/lib/hexToRgba';
import useGetPosition from '@/lib/hooks/useGetPosition';
import useWindowSize from '@/lib/hooks/useWIndowSize';
import { motion, useAnimation } from 'framer-motion';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

const VerticalSlider = ({ setData, dataKey }: { setData: Dispatch<SetStateAction<Record<string, string>>>, dataKey: string }) => {
    const values = Array.from({ length: 8 }, (_, index) => String(7 - index));
    const CANVAS_HEIGHT = Math.min(200, 400); // Change to height
    const DEFAULT_RADIUS = 10;
    const CANVAS_WIDTH = 180
    const OFFSET = 0;
    const CANVAS_HEIGHT_WITH_OFFSET_X2 = CANVAS_HEIGHT + OFFSET * 2; // Change to height
    const CANVAS_HEIGHT_WITH_OFFSET = CANVAS_HEIGHT + OFFSET; // Change to height
    const TRIANGLE_START_X = 0; // Change to start X
    const TRIANGLE_END_X = 100; // Change to end X
    const SLIDER_X = TRIANGLE_END_X + 80; // Change to X

    const NO_OF_PARTS = values?.length - 1;
    const point = Math.round(CANVAS_HEIGHT / NO_OF_PARTS)
    const { position: sliderPosition, divRef: sliderRef } = useGetPosition();
    const [radius, setRadius] = useState(DEFAULT_RADIUS);
    const [cursorPosition, setCursorPosition] = useState(point * 3 + OFFSET); // Change initial position
    const pointStart = sliderPosition.y; // Change to y

    const snappingPointsArr = useMemo(() => {
        const points: number[] = [];
        for (let i = pointStart + OFFSET, j = 0; i <= CANVAS_HEIGHT + pointStart + OFFSET; i += point, j++) {
            points.push(i);
        }
        return points;
    }, [point, pointStart]);

    const valueToPointMap = useMemo(() => {
        const valuesMap = {};
        for (let i = pointStart + OFFSET, j = 0; i <= CANVAS_HEIGHT + pointStart + OFFSET; i += point, j++) {
            valuesMap[i - pointStart] = values[j];
        }
        return valuesMap;
    }, [point, pointStart]);

    const snappingPoint = snappingPointsArr.map((el, idx) => {
        const y = el - pointStart;
        if (y === 0) {
            return <></>
        }
        return <rect key={el} x={0} y={y} r="4" width={50} height={4} fill="white" strokeWidth="2" opacity={1} />;
    });

    const onDragEnd = () => {
        setRadius(DEFAULT_RADIUS);
    };

    const onDrag = (event, info) => {
        const currentMousePoint = info.point.y - pointStart; // Change to y
        const closestSnapIndex = snappingPointsArr.reduce((closestIndex, point, currentIndex) => {
            const pointWithoutOffset = point - pointStart;
            const closestWithoutOffset = snappingPointsArr[closestIndex] - pointStart;
            const distance = Math.abs(currentMousePoint - pointWithoutOffset);
            const distanceToClosest = Math.abs(currentMousePoint - closestWithoutOffset);
            return distance < distanceToClosest ? currentIndex : closestIndex;
        }, 0);
        const closestSnapPoint = snappingPointsArr[closestSnapIndex] - pointStart;
        setData(p => {
            // localStorage.setItem('bioFormData', JSON.stringify({ ...p, [dataKey]: valueToPointMap[closestSnapPoint] }));
            return { ...p, [dataKey]: valueToPointMap[closestSnapPoint] }
        })
        setRadius(DEFAULT_RADIUS * 1.2);
        setCursorPosition(closestSnapPoint);
    };

    const handleClick = (event) => {
        const clickY = event.clientY - pointStart; // Change to y

        // Find the closest snap point on click
        const closestSnapIndex = snappingPointsArr.reduce((closestIndex, point, currentIndex) => {
            const pointWithoutOffset = point - pointStart;
            const closestWithoutOffset = snappingPointsArr[closestIndex] - pointStart;
            const distance = Math.abs(clickY - pointWithoutOffset);
            const distanceToClosest = Math.abs(clickY - closestWithoutOffset);
            return distance < distanceToClosest ? currentIndex : closestIndex;
        }, 0);
        setData(p => {
            localStorage.setItem('bioFormData', JSON.stringify({ ...p, [dataKey]: valueToPointMap[closestSnapPoint] }));
            return { ...p, [dataKey]: valueToPointMap[closestSnapPoint] }
        })
        const closestSnapPoint = snappingPointsArr[closestSnapIndex] - pointStart;
        setCursorPosition(closestSnapPoint);
    };

    useEffect(() => {
        let localFormData

        const storedData = localStorage?.getItem('bioFormData');
        if (storedData) {
            localFormData = JSON.parse(storedData)
        }
        setData(p => {
            localStorage.setItem('bioFormData', JSON.stringify({ ...localFormData, [dataKey]: valueToPointMap[cursorPosition] }));
            return { ...p, [dataKey]: valueToPointMap[cursorPosition] }
        })
    }, [cursorPosition]);

    const liquidRectPath = `M ${TRIANGLE_START_X} ${CANVAS_HEIGHT + OFFSET - 20} L ${300} ${CANVAS_HEIGHT + OFFSET - 20} L ${300} ${cursorPosition} L ${TRIANGLE_START_X} ${cursorPosition} Z`;

    const glassShape = `M ${10} ${OFFSET} L ${CANVAS_WIDTH - 10} ${OFFSET} L ${CANVAS_WIDTH - 40} ${CANVAS_HEIGHT + OFFSET - 20} L ${TRIANGLE_START_X + 50} ${CANVAS_HEIGHT + OFFSET - 20} Z`;
    // const glassShape = `M ${10} ${OFFSET} L ${TRIANGLE_START_X + 50} ${CANVAS_HEIGHT + OFFSET}  L ${CANVAS_WIDTH - 80} ${CANVAS_HEIGHT + OFFSET}   L ${CANVAS_WIDTH - 40} ${OFFSET}`;
    const glassShapeBg = `M ${0} ${OFFSET} L ${CANVAS_WIDTH} ${OFFSET} L ${CANVAS_WIDTH - 30} ${CANVAS_HEIGHT + OFFSET - 10} L ${TRIANGLE_START_X + 40} ${CANVAS_HEIGHT + OFFSET - 10} Z`;

    const handleIncrease = () => {
        setCursorPosition(p => {
            if ((p - point) in valueToPointMap) {
                return p - point
            }
            else {
                return p
            }
        })
    }

    const handleDecrease = () => {
        setCursorPosition(p => {
            if ((p + point) in valueToPointMap) {
                return p + point
            }
            else {
                return p
            }
        })

    }
    return (
        <div ref={sliderRef}>
            <motion.div initial={{ textAlign: "center", width: 100, userSelect: 'none', fontWeight: "bold", borderRadius: 10, color: "white", backgroundColor: "#3AB0C9", }} animate={{ y: cursorPosition - 12, x: CANVAS_WIDTH / 2 - 50 }} transition={{ duration: 0 }}>{valueToPointMap[cursorPosition]} {valueToPointMap[cursorPosition] > 1 ? "glasses" : "glass"} {valueToPointMap[cursorPosition] == 7 ? "+" : ''}</motion.div>
            <motion.svg height={CANVAS_HEIGHT_WITH_OFFSET_X2} width={CANVAS_WIDTH} style={{ cursor: "pointer", backgroundColor: "white" }} onClick={handleClick}>
                <mask id="glassShapedMask" x="0" y="0" width="100%" height="100%">
                    <path d={glassShape} fill="white" stroke="black" strokeWidth={0} />
                </mask>
                <path d={glassShapeBg} fill="#ddd" opacity={0.4} strokeLinejoin="round" strokeLinecap='round' />
                <g>
                    {snappingPoint}
                </g>
                <path d={glassShape} fill="transparent" stroke="#222" opacity={0.4} strokeWidth={0} strokeLinejoin="round" strokeLinecap='round' />
                <motion.path animate={{ d: liquidRectPath }} fill="white" stroke="black" strokeWidth={0} mask="url(#glassShapedMask)" />
                <motion.path animate={{ d: liquidRectPath }} fill="url(#gradientId)" stroke="black" strokeWidth={0} mask="url(#glassShapedMask)" />
                <defs>
                    <linearGradient id={"gradientId"} x1="0%" y1="0%" x2="10%" y2="100%">
                        <stop offset="20%" style={{ stopColor: hexToRgba('#3AB0C9', 0.6) }} />
                        <stop offset="100%" style={{ stopColor: hexToRgba('#2072F3', 0.6) }} />
                    </linearGradient>
                </defs>
            </motion.svg>
            <motion.div className='flex gap-8 justify-center my-8'>
                <motion.div className=' px-4 text-3xl rounded-xl font-semibold bg-gray-200 cursor-pointer' whileTap={{ scale: 0.8 }} onClick={handleDecrease}>-</motion.div>
                <motion.div className='flex justify-center items-center text-2xl font-semibold'>{valueToPointMap[cursorPosition]}</motion.div>
                <motion.div whileTap={{ scale: 0.8 }} className='flex items-center font-semibold px-4 text-2xl rounded-xl bg-gray-200 cursor-pointer' onClick={handleIncrease}>+</motion.div>
            </motion.div>
        </div>
    );
}

export default VerticalSlider;
